import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Menu, X, Globe, User, LayoutDashboard, FileText, LogOut, ChevronDown, ShieldAlert, Sparkles, Moon, Sun } from 'lucide-react';
import { Button } from './ui/button';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel } from './ui/dropdown-menu';

const THEME_KEY = 'iacc_theme';
const applyTheme = (id) => {
  const html = document.documentElement;
  const body = document.body;
  ['default', 'slate-1', 'slate-2', 'slate-3', 'slate-4', 'green', 'dark'].forEach((t) => body.classList.remove(`theme-${t}`));
  if (!id || id === 'default') {
    html.removeAttribute('data-theme');
  } else {
    html.setAttribute('data-theme', id);
    body.classList.add(`theme-${id}`);
  }
};

const Navbar = () => {
  const { t, lang, toggle, dir } = useLang();
  const { user, logout, isAuthed } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isDark, setIsDark] = useState(false);
  const loc = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const saved = localStorage.getItem(THEME_KEY);
    setIsDark(saved === 'dark');
  }, []);

  const toggleDark = () => {
    const next = !isDark;
    setIsDark(next);
    const themeId = next ? 'dark' : 'default';
    localStorage.setItem(THEME_KEY, themeId);
    applyTheme(themeId);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const links = [
    { to: '/', label: t('nav.home') },
    { to: '/Comparison', label: t('nav.compare') },
    { to: '/Services', label: t('nav.services') },
    { to: '/accounting/pricing', label: 'AI Accounting' },
    { to: '/SetupWizard', label: t('nav.wizard') },
    { to: '/About', label: t('nav.about') },
    { to: '/Contact', label: t('nav.contact') }
  ];

  const onLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  const initial = user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U';

  const inAccountingApp = /^\/accounting(\/team)?\/?$/.test(loc.pathname);
  if (inAccountingApp && isAuthed) return null;

  return (
    <header className="fixed inset-x-0 z-50 pointer-events-none px-3 sm:px-6" style={{ top: 'var(--trial-bar-h, 0px)' }} dir={dir}>
      <div className={`nav-pill pointer-events-auto max-w-7xl mx-auto mt-3 px-3 sm:px-4 lg:px-5 h-16 rounded-full flex items-center justify-between gap-4 bg-white/85 backdrop-blur-xl border border-[#2563eb]/15 transition-all duration-500 ${scrolled ? 'shadow-xl shadow-blue-900/10' : 'shadow-lg shadow-blue-900/5'} ${open ? 'rounded-b-none rounded-t-3xl lg:rounded-full' : ''}`} data-testid="navbar-pill">
        <Link to="/" className="flex items-center gap-3 group">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#3b82f6] to-[#1d4ed8] flex items-center justify-center shadow-lg shadow-[#2563eb]/20 group-hover:shadow-[#2563eb]/40 transition-shadow">
            <span className="text-[#f8fafc] font-display text-2xl font-bold leading-none">i</span>
          </div>
          <div className="leading-tight">
            <div className="text-[#0a0a0a] font-display text-lg tracking-wide">{t('brand')}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#2563eb]/70">{t('brand_sub')}</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <Link key={l.to} to={l.to} className={`px-3 py-2 text-[12.5px] tracking-wide whitespace-nowrap rounded-full transition-colors ${loc.pathname === l.to ? 'text-[#3b82f6] bg-[#2563eb]/10' : 'text-[#111827]/75 hover:text-[#3b82f6]'}`}>
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={toggleDark}
            className="flex items-center justify-center w-9 h-9 rounded-full border border-[#2563eb]/20 text-[#111827]/70 hover:text-[#3b82f6] hover:border-[#3b82f6] transition-colors"
            title={isDark ? (lang === 'ar' ? 'الوضع الفاتح' : 'Light mode') : (lang === 'ar' ? 'الوضع الليلي' : 'Dark mode')}
            data-testid="dark-mode-toggle"
            aria-label="Toggle dark mode"
          >
            {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <button onClick={toggle} className="hidden md:flex items-center gap-1.5 px-3 py-2 text-xs uppercase tracking-widest text-[#111827]/70 hover:text-[#3b82f6] transition-colors border border-[#2563eb]/20 rounded-full">
            <Globe className="w-3.5 h-3.5" />
            <span>{lang === 'en' ? 'AR' : 'EN'}</span>
          </button>

          {isAuthed ? (
            <DropdownMenu>
              <DropdownMenuTrigger className="hidden md:flex items-center gap-2 h-10 px-2 pe-3 rounded-full hover:bg-[#2563eb]/10 transition-colors focus:outline-none">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#2563eb] to-[#1d4ed8] flex items-center justify-center text-[#f8fafc] font-medium text-sm overflow-hidden">
                  {user?.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : initial}
                </div>
                <ChevronDown className="w-3.5 h-3.5 text-[#2563eb]" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#ffffff] border-[#2563eb]/25 text-[#111827] min-w-[220px]">
                <DropdownMenuLabel className="text-[#2563eb]/80 text-xs uppercase tracking-widest">
                  <div className="text-[#0a0a0a] normal-case text-sm">{user?.name}</div>
                  <div className="text-[10px] text-[#111827]/50 mt-0.5 truncate">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-[#2563eb]/15" />
                <DropdownMenuItem onClick={() => navigate('/Dashboard')} className="cursor-pointer focus:bg-[#2563eb]/10 focus:text-[#3b82f6]">
                  <LayoutDashboard className="w-4 h-4 me-2" /> {t('auth.dashboard')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/accounting')} className="cursor-pointer focus:bg-[#2563eb]/10 focus:text-[#3b82f6]" data-testid="nav-ai-accounting">
                  <Sparkles className="w-4 h-4 me-2 text-[#2563eb]" /> {lang === 'ar' ? 'المحاسبة الذكية' : 'AI Accounting'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/MyApplications')} className="cursor-pointer focus:bg-[#2563eb]/10 focus:text-[#3b82f6]">
                  <FileText className="w-4 h-4 me-2" /> {t('nav.apps')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/Profile')} className="cursor-pointer focus:bg-[#2563eb]/10 focus:text-[#3b82f6]">
                  <User className="w-4 h-4 me-2" /> {t('auth.profile')}
                </DropdownMenuItem>
                {user?.is_admin && (
                  <>
                    <DropdownMenuSeparator className="bg-[#2563eb]/15" />
                    <DropdownMenuItem onClick={() => navigate('/admin')} className="cursor-pointer focus:bg-[#2563eb]/10 text-[#3b82f6]">
                      <ShieldAlert className="w-4 h-4 me-2" /> {lang === 'ar' ? 'لوحة الأدمن' : 'Admin Panel'}
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator className="bg-[#2563eb]/15" />
                <DropdownMenuItem onClick={onLogout} className="cursor-pointer text-rose-400 focus:bg-rose-500/10 focus:text-rose-300">
                  <LogOut className="w-4 h-4 me-2" /> {t('auth.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link to="/login" className="hidden md:inline-flex">
                <Button className="ghost-button rounded-full h-10 px-5 text-[13px] bg-transparent">{t('auth.login')}</Button>
              </Link>
              <Link to="/register" className="hidden md:inline-flex">
                <Button className="gold-button rounded-full h-10 px-5 text-[13px] font-medium">{t('auth.register')}</Button>
              </Link>
            </>
          )}

          <button className="lg:hidden p-2 text-[#3b82f6]" onClick={() => setOpen(!open)}>
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile */}
      {open && (
        <div className="nav-pill pointer-events-auto lg:hidden max-w-7xl mx-auto bg-white/95 backdrop-blur-xl border border-t-0 border-[#2563eb]/15 rounded-b-3xl shadow-xl shadow-blue-900/10" data-testid="navbar-mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <Link key={l.to} to={l.to} className={`px-4 py-3 rounded-md text-sm ${loc.pathname === l.to ? 'text-[#3b82f6] bg-[#2563eb]/10' : 'text-[#111827]/80'}`}>
                {l.label}
              </Link>
            ))}
            {isAuthed && (
              <>
                <Link to="/Dashboard" className="px-4 py-3 rounded-md text-sm text-[#111827]/80">{t('auth.dashboard')}</Link>
                <Link to="/accounting" className="px-4 py-3 rounded-md text-sm text-[#111827]/80 flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#2563eb]" /> {lang === 'ar' ? 'المحاسبة الذكية' : 'AI Accounting'}</Link>
                <Link to="/MyApplications" className="px-4 py-3 rounded-md text-sm text-[#111827]/80">{t('nav.apps')}</Link>
                <Link to="/Profile" className="px-4 py-3 rounded-md text-sm text-[#111827]/80">{t('auth.profile')}</Link>
              </>
            )}
            <button onClick={toggle} className="mt-2 px-4 py-3 rounded-md text-sm text-[#3b82f6] border border-[#2563eb]/30 flex items-center gap-2">
              <Globe className="w-4 h-4" /> {lang === 'en' ? 'العربية' : 'English'}
            </button>
            {isAuthed ? (
              <Button onClick={onLogout} className="w-full mt-2 rounded-md ghost-button bg-transparent border-rose-400/30 text-rose-400">
                <LogOut className="w-4 h-4 me-2" /> {t('auth.logout')}
              </Button>
            ) : (
              <>
                <Link to="/login"><Button className="ghost-button w-full mt-2 rounded-md bg-transparent">{t('auth.login')}</Button></Link>
                <Link to="/register"><Button className="gold-button w-full mt-2 rounded-md">{t('auth.register')}</Button></Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
